import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import Modal from '/ui/Modal';

import { setIframeQuery } from '../../../../../helpers/iframe';

import {
  PROFILE_LOGIN_IFRAME_NAME,
  PROFILE_LOGIN_PATH,
  PROFILE_REGISTRATION_PATH,
} from '../../../../../constants/profile';
import { MY_FILES_PATH } from '../../../../../constants/routes';
import { useTheme } from '../../../../../context/Theme';
import { loadLoginData } from '../../../../client/actions/Client';
import { useHomeMessageId } from '../../../hooks/useHomeMessageId';
import { HomeAuthModalMode } from '../HomeAuthModalContext';

import { AppDispatch, ReduxState } from '../../../../../types/app';

const normalizeProfileHost = (profileHost?: string) => {
  const host = profileHost?.trim();

  if (!host) return null;

  return /^https?:\/\//i.test(host) ? host : `https://${host}`;
};

type Props = {
  mode: HomeAuthModalMode;
  visible: boolean;
  handleCloseModal: () => void;
};

const LoginModal = ({ mode, visible, handleCloseModal }: Props) => {
  const intl = useIntl();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const profileHost = useSelector(
    (state: ReduxState) => state.Data.config?.profileHost
  );
  const homeMessageId = useHomeMessageId();

  const authIframeUrl = useMemo(() => {
    const normalizedProfileHost = normalizeProfileHost(profileHost);

    if (!normalizedProfileHost) return null;

    const authPath =
      mode === 'register' ? PROFILE_REGISTRATION_PATH : PROFILE_LOGIN_PATH;
    const url = new URL(authPath, normalizedProfileHost);
    setIframeQuery(url, intl.locale, theme);

    return url.toString();
  }, [intl.locale, mode, profileHost, theme]);

  const authOrigin = useMemo(() => {
    if (!authIframeUrl) return null;

    return new URL(authIframeUrl).origin;
  }, [authIframeUrl]);

  useEffect(() => {
    if (!visible) return undefined;

    const handleMessage = (event: MessageEvent) => {
      if (!authOrigin || event.origin !== authOrigin) return;

      if (event.data === 'reload') {
        void dispatch(loadLoginData())
          .then(() => {
            handleCloseModal();
            navigate(MY_FILES_PATH);
          })
          .catch((err: unknown) => {
            console.error('loadLoginData:', err);
          });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [visible, authOrigin, dispatch, handleCloseModal, navigate]);

  return (
    <Modal
      className="home-page__login-modal"
      open={visible}
      onCancel={handleCloseModal}
      footer={null}
      centered
      destroyOnHidden
      width={480}
    >
      <iframe
        key={mode}
        className="home-page__login-iframe autoHeight-iframe"
        name={PROFILE_LOGIN_IFRAME_NAME}
        src={authIframeUrl || undefined}
        title={intl.formatMessage({
          id:
            mode === 'register'
              ? homeMessageId('button.createAccount')
              : homeMessageId('header.login'),
        })}
        height={400}
      />
    </Modal>
  );
};

export default LoginModal;
